import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "src/components/dashboard";
import searchIcon from "src/assets/images/input/searchIcon.svg";
import addIcon from "src/assets/images/common/add.svg";
import PartInput from "src/components/inputs/partsearch";
import AddCategoryModal from "./AddCategoryModal";
import GlobalAnalytics from "src/components/partners/GlobalAnalytics";
import { useLoading } from "src/hooks";
import { useLazyDumpPartnersDataQuery } from "src/store/services/artisanApi";
import { auth } from "src/store/Auth";
import { theme } from "src/styles/Theme";
import { useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import AppModal from "src/components/ui/widget/Modal/Modal";
import ModalContent from "src/components/common/ModalContent";
import exportImg from '../../assets/images/modal/Icon.png';
import ImagePopUp from "src/components/ui/ImagePopUP";
import {
  Button,
  ButtonClass,
  ConfirmationModal,
  Flex,
} from "src/components/ui";
import CategoryCard from "src/components/partners/CategoryCard";
import { SendNotificationModal } from "src/components/users";
import {
  useGetAllPartnerCategoriesQuery,
  useDeletePartnerCategoryMutation,
  PartnerCategory,
} from "src/store/services/partnerApi";

const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 2.5V12.5M10 12.5L6.25 8.75M10 12.5L13.75 8.75M2.5 15H17.5"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Rebrand = () => {
  const navigate = useNavigate();
  const {
    data: partnerCategoriesResponse,
    isLoading,
    error,
    refetch,
  } = useGetAllPartnerCategoriesQuery();
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [isOccupManager, setIsOccupManager] = useState(false);
  const { authUser } = auth.use();
  const [triggerDumpArtisansData] = useLazyDumpPartnersDataQuery();
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    if (authUser?.role?.includes("occupationalManager")) {
      setIsOccupManager(true);
    }
  }, [authUser]);
  useEffect(() => {
    document.title = "Partner's Page";
  }, []);

  const [editCategory, setEditCategory] = useState<PartnerCategory | null>(
    null,
  );
  const [deleteCategory, setDeleteCategory] = useState<PartnerCategory | null>(
    null,
  );
  const [deletePartnerCategory, { isLoading: isDeleting }] =
    useDeletePartnerCategoryMutation();

  // Get categories from API response
  const categories = useMemo(() => {
    const data = partnerCategoriesResponse?.payload?.data || [];
    // Filter out any invalid categories (missing _id or name)
    return data.filter(
      (category: PartnerCategory) =>
        category && category.category_id && category.category_name,
    );
  }, [partnerCategoriesResponse]);

  const filteredCategories = useMemo(() => {
    return categories.filter((category: PartnerCategory) => {
      if (!category || !category.category_name) return false;
      return category.category_name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    });
  }, [categories, searchQuery]);

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export partners data");
    setShowExportModal(!showExportModal);
  };

  const handleAddCategory = () => {
    setIsModalOpen(true);
  };

  const handleCategoryAdded = () => {
    refetch();
    setEditCategory(null);
  };

  const handleEdit = (category: PartnerCategory) => {
    setEditCategory(category);
    setIsModalOpen(true);
  };

  const handleDelete = (category: PartnerCategory) => {
    setDeleteCategory(category);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCategory) return;

    try {
      await deletePartnerCategory(deleteCategory.category_id).unwrap();
      setDeleteCategory(null);
      refetch();
    } catch (err: any) {
      console.error("Failed to delete category:", err);
      alert(
        err?.data?.message || "Failed to delete category. Please try again.",
      );
    }
  };

  const handleView = (category: PartnerCategory) => {
    navigate(`/partners/${category.category_id}`);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditCategory(null);
  };

  const {
    loading: exportingData,
    startLoading: startExportingData,
    stopLoading: stopExportingData,
  } = useLoading(false);

  const dumpArtisanData = async () => {
    startExportingData();
    try {
      const res: any = await triggerDumpArtisansData().unwrap();
      setShowExportModal(false);
      toast.success(res?.message);
      const url = res?.payload?.data?.fileUrl;
      if (url) window.location.href = url;
    } catch (err: any) {
      toast.error(err?.data?.error?.message || "Export failed");
    } finally {
      stopExportingData();
    }
  };

  return (
    <DashboardLayout pageTitle="">
      <SendNotificationModal
        open={open}
        userId={[""]}
        handleClose={handleClose}
        artisan={"partner"}
      />
      <div className="bg-[#F2F4F7] min-h-[calc(100vh-80px)]">
        <div className="mb-6">
          <div className="flex gap-3 items-center justify-between">
            <div>
            <div
              style={{ fontWeight: "600" }}
              className="text-[32px] text-[#3a3541] mb-2"
            >
              Total Partners
            </div>
            <div className="text-base text-[#45556C] mb-6">
              {isLoading
                ? "Loading..."
                : `${categories.length} Partner Categor${
                    categories.length === 1 ? "y" : "ies"
                  }`}
            </div>
            </div>
            <div className="min-w-[300px]" style={{'fontFamily':'Raleway'}}>
              <PartInput
                icon={<img src={searchIcon} alt="search" />}
                type="search"
                placeholder="Search categories..."
                handleChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              {/* Export button */}
              <Button
                onClick={handleExport}
                classes={[ButtonClass.SOLID, ButtonClass.WITH_ICON]}
                style={{
                  backgroundColor: theme.colors.white,
                  height: "48px",
                  color: 'black',
                  minWidth: "145px",
                  border: '1.33px solid #0000001A',
                }}
                className={`${isOccupManager ? "!hidden" : ""}`}
              >
                <img src={exportImg} alt="" />
                <span>Export</span>
              </Button>
              <button
                onClick={handleAddCategory}
                className="bg-[#7c2ebd] h-12 min-w-[200px] flex items-center justify-center gap-2 text-white rounded-xl px-4 py-2"
              >
                <img src={addIcon} alt="add" width={24} height={24} />
                <span className="text-white">Add New Category</span>
              </button>

              <Button
                onClick={handleOpen}
                classes={[ButtonClass.SOLID, ButtonClass.WITH_ICON]}
                style={{
                  backgroundColor: theme.colors.purple,
                  height: "48px",
                  width: "max-content",
                }}
                className={`${isOccupManager ? "!hidden" : ""}`}
              >
                <span>Notify all partners</span>
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600">
              Failed to load categories. Please try again.
            </p>
          </div>
        )}
        <GlobalAnalytics partner_id={""}/>
        {isLoading ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm animate-pulse"
              >
                <div className="h-20 bg-gray-200 rounded mb-4"></div>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="mt-6 p-8 bg-white rounded-xl text-center">
            <p className="text-gray-500">
              {searchQuery
                ? "No categories found matching your search."
                : "No partner categories yet. Add your first category to get started."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
            {filteredCategories.map((category: PartnerCategory) => {
              if (
                !category ||
                !category.category_id ||
                !category.category_name
              ) {
                return null;
              }
              return (
                <CategoryCard
                  key={category.category_id}
                  category={category}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onView={handleView}
                />
              );
            })}
          </div>
        )}
      </div>

      <AppModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        content={
          <ModalContent
            content1="Export Partners Data"
            content2="Are you sure you want to export patners data?"
            btnAction={dumpArtisanData}
            linkContent={"Yes, Please"}
            onClick={() => setShowExportModal(false)}
            loading={exportingData}
          />
        }
      />
      <AddCategoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddCategory={handleCategoryAdded}
        editCategory={editCategory}
      />

      {/* Delete Confirmation Modal */}
      {deleteCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Delete Category
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{deleteCategory.category_name}
                "? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setDeleteCategory(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Rebrand;
